import React from 'react'
import type { TrackedBetSlip, BetSelection } from '../context/BetContext'
import type { FixtureLite } from '../types'
import {
  decimalToFractional,
  decimalOddsToImpliedProbability,
  calculateReturn,
  formatDecimalOdds,
  formatCurrency,
  calculateValue,
} from '../utils/odds'

interface TrackedBetCardProps {
  bet: TrackedBetSlip
  fixtureMap: Map<number, FixtureLite>
  onSettle: (id: string, status: 'won' | 'lost') => void
  onRemove: (id: string) => void
}

// Get the current live value for a bet selection from the fixture
function getLiveProgress(
  selection: BetSelection,
  fixture: FixtureLite | undefined
): { current: number; target: number; isLive: boolean; status: 'live' | 'ft' | 'ns' } | null {
  if (!fixture) return null
  if (fixture.status !== 'LIVE' && fixture.status !== 'FT') return null

  // Player bets cannot show live progress - we don't have individual player stats
  if (selection.scope === 'player') {
    return null
  }

  // No line means no target to track (e.g., match winner bets)
  if (selection.line == null) {
    return null
  }

  const homeStats = fixture.home_stats
  const awayStats = fixture.away_stats
  const isLive = fixture.status === 'LIVE'

  // Helper to get stat value based on teamType (home, away, or total)
  const getStatValue = (homeStat: number, awayStat: number): number => {
    const teamType = selection.teamType ?? 'total'
    switch (teamType) {
      case 'home':
        return homeStat
      case 'away':
        return awayStat
      case 'total':
      default:
        return homeStat + awayStat
    }
  }

  // For goals, we use the score directly (always available)
  if (selection.marketType === 'goals') {
    const current = getStatValue(fixture.home_score ?? 0, fixture.away_score ?? 0)
    return {
      current,
      target: selection.line,
      isLive,
      status: isLive ? 'live' : 'ft',
    }
  }

  // For other stats, we need live stats data
  // If no stats data, show 0 progress for live/FT games
  let current = 0
  switch (selection.marketType) {
    case 'corners':
      current = getStatValue(homeStats?.corners ?? 0, awayStats?.corners ?? 0)
      break
    case 'shots':
      current = getStatValue(homeStats?.shots ?? 0, awayStats?.shots ?? 0)
      break
    case 'shots_on_target':
      current = getStatValue(homeStats?.shots_on_target ?? 0, awayStats?.shots_on_target ?? 0)
      break
    case 'cards':
      current = getStatValue(
        (homeStats?.yellows ?? 0) + (homeStats?.reds ?? 0),
        (awayStats?.yellows ?? 0) + (awayStats?.reds ?? 0)
      )
      break
    default:
      // For 'other' market type, we can't track live progress
      return null
  }

  return {
    current,
    target: selection.line,
    isLive,
    status: isLive ? 'live' : 'ft',
  }
}

// Check if a selection has won or lost based on current progress
function checkSelectionResult(
  selection: BetSelection,
  fixture: FixtureLite | undefined
): 'won' | 'lost' | 'pending' {
  if (!fixture || fixture.status !== 'FT') return 'pending'

  const progress = getLiveProgress(selection, fixture)
  if (!progress) return 'pending'

  // For Over bets: current must be STRICTLY GREATER than target to win
  // For Under bets: current must be STRICTLY LESS than target to win
  if (selection.isOver) {
    return progress.current > progress.target ? 'won' : 'lost'
  } else {
    return progress.current < progress.target ? 'won' : 'lost'
  }
}

// Check if the entire bet slip has won or lost (all selections must complete)
export function checkBetResult(
  bet: TrackedBetSlip,
  fixtureMap: Map<number, FixtureLite>
): 'won' | 'lost' | 'pending' {
  const results = bet.selections.map((sel) => {
    const fixture = fixtureMap.get(sel.fixtureId)
    return checkSelectionResult(sel, fixture)
  })

  // If any selection is still pending, the whole bet is pending
  if (results.includes('pending')) return 'pending'

  // If any selection lost, the whole accumulator loses
  if (results.includes('lost')) return 'lost'

  // All selections won
  return 'won'
}

function getConfidenceStyle(confidence: string) {
  switch (confidence) {
    case 'high':
      return { background: 'rgba(74, 222, 128, 0.15)', color: 'var(--green)' }
    case 'med':
      return { background: 'rgba(251, 191, 36, 0.15)', color: 'var(--yellow)' }
    default:
      return { background: 'rgba(251, 113, 133, 0.15)', color: 'var(--red)' }
  }
}

function getStatusStyle(status: string) {
  switch (status) {
    case 'won':
      return { background: 'rgba(74, 222, 128, 0.2)', color: 'var(--green)' }
    case 'lost':
      return { background: 'rgba(251, 113, 133, 0.2)', color: 'var(--red)' }
    default:
      return { background: 'rgba(251, 191, 36, 0.2)', color: 'var(--yellow)' }
  }
}

// Calculate combined probability for accumulator
function calculateCombinedProbability(selections: BetSelection[]): number {
  if (selections.length === 0) return 0
  const combined = selections.reduce((acc, sel) => acc * (sel.confidencePct / 100), 1)
  return combined * 100
}

export function TrackedBetCard({ bet, fixtureMap, onSettle, onRemove }: TrackedBetCardProps) {
  const statusStyle = getStatusStyle(bet.status)
  const combinedProbability = calculateCombinedProbability(bet.selections)

  const impliedProb = bet.oddsDecimal ? decimalOddsToImpliedProbability(bet.oddsDecimal) : null
  const value = bet.oddsDecimal ? calculateValue(combinedProbability, bet.oddsDecimal) : null
  const potentialReturn = bet.stake && bet.oddsDecimal ? calculateReturn(bet.stake, bet.oddsDecimal) : null

  return (
    <div className={`tracked-bet-card ${bet.status}`}>
      <div className="tracked-bet-header">
        <div className="tracked-bet-count">
          {bet.selections.length} Leg{bet.selections.length !== 1 ? 's' : ''}
        </div>
        <div className="tracked-bet-status" style={statusStyle}>
          {bet.status}
        </div>
      </div>

      <div className="tracked-bet-selections">
        {bet.selections.map((sel) => {
          const confStyle = getConfidenceStyle(sel.confidence)
          const fixture = fixtureMap.get(sel.fixtureId)
          const progress = getLiveProgress(sel, fixture)
          const selectionResult = fixture ? checkSelectionResult(sel, fixture) : 'pending'

          // Determine if this leg is won/lost based on FT result
          const isWon = selectionResult === 'won'
          const isLost = selectionResult === 'lost'

          // Determine progress display color
          const getProgressColor = () => {
            if (!progress) return ''
            if (progress.isLive) {
              // During live, show green if currently winning, red if losing
              const currentlyWinning = sel.isOver
                ? progress.current > progress.target
                : progress.current < progress.target
              return currentlyWinning ? 'winning' : 'losing'
            }
            return progress.status // 'ft'
          }

          return (
            <div key={sel.id} className={`tracked-selection ${isWon ? 'leg-won' : ''} ${isLost ? 'leg-lost' : ''}`}>
              <div className="tracked-selection-fixture">{sel.fixture}</div>
              <div className="tracked-selection-pick">
                <span className="tracked-selection-title">{sel.title}</span>
                <span className="tracked-selection-line">{sel.isOver ? 'Over' : 'Under'} {sel.line}</span>
                {progress ? (
                  <span className={`tracked-selection-progress ${getProgressColor()}`}>
                    {progress.current}/{sel.line}
                  </span>
                ) : (
                  <span className="tracked-selection-prob" style={confStyle}>{sel.confidencePct.toFixed(0)}%</span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <div className="tracked-bet-odds-section">
        <div className="odds-row">
          <div className="odds-label">Your Odds</div>
          <div className="odds-values">
            {bet.oddsDecimal ? (
              <>
                <span className="odds-decimal">{formatDecimalOdds(bet.oddsDecimal)}</span>
                <span className="odds-fractional">({decimalToFractional(bet.oddsDecimal)})</span>
              </>
            ) : (
              <span className="odds-na">-</span>
            )}
          </div>
        </div>

        <div className="odds-row">
          <div className="odds-label">Implied Prob</div>
          <div className="odds-values">
            {impliedProb !== null ? (
              <span className="odds-implied">{impliedProb.toFixed(1)}%</span>
            ) : (
              <span className="odds-na">-</span>
            )}
          </div>
        </div>

        <div className="odds-row">
          <div className="odds-label">Our Probability</div>
          <div className="odds-values">
            <span className="odds-our">{combinedProbability.toFixed(1)}%</span>
          </div>
        </div>

        {value !== null && (
          <div className="odds-row value-row">
            <div className="odds-label">Value</div>
            <div className="odds-values">
              <span className={`value-indicator ${value >= 0 ? 'positive' : 'negative'}`}>
                {value >= 0 ? '+' : ''}{value.toFixed(1)}%
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="tracked-bet-stake-section">
        <div className="stake-row">
          <span className="stake-label">Stake</span>
          <span className="stake-value">{bet.stake ? formatCurrency(bet.stake) : '-'}</span>
        </div>
        <div className="stake-row returns">
          <span className="stake-label">Returns</span>
          <span className="stake-value returns-value">
            {potentialReturn ? formatCurrency(potentialReturn) : '-'}
          </span>
        </div>
      </div>

      {bet.status === 'pending' && (
        <div className="settle-buttons">
          <button
            className="settle-btn won"
            onClick={() => onSettle(bet.id, 'won')}
          >
            Won
          </button>
          <button
            className="settle-btn lost"
            onClick={() => onSettle(bet.id, 'lost')}
          >
            Lost
          </button>
        </div>
      )}

      {bet.status !== 'pending' && (
        <div className="settled-info">
          <span className="settled-label">
            {bet.status === 'won' ? 'Profit' : 'Loss'}:
          </span>
          <span className={`settled-amount ${bet.status}`}>
            {bet.status === 'won' && bet.stake && bet.oddsDecimal
              ? `+${formatCurrency(bet.stake * (bet.oddsDecimal - 1))}`
              : bet.stake
              ? `-${formatCurrency(bet.stake)}`
              : '-'}
          </span>
        </div>
      )}

      <button
        className="remove-tracked-btn"
        onClick={() => onRemove(bet.id)}
        title="Remove bet"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      </button>
    </div>
  )
}
